import * as React from 'react';
import { Icon, mergeStyles } from '@fluentui/react';

export type Tone = 'success' | 'info' | 'warning' | 'neutral' | 'danger';

interface ToneStyle {
    bg: string;
    fg: string;
    border: string;
}

// Fluent-aligned palette mirroring the v9 Badge semantic tones (success, informative,
// warning, danger, subtle). Light tint background + saturated foreground keeps pills
// readable in dense table cells. All fg/bg pairs meet WCAG AA (>=4.5:1) for 12px
// semibold text.
const tones: Record<Tone, ToneStyle> = {
    success: { bg: '#dff6dd', fg: '#0b6a0b', border: '#a6d6a4' },
    info:    { bg: '#deecf9', fg: '#005a9e', border: '#9fc6e8' },
    warning: { bg: '#fff4ce', fg: '#6b5400', border: '#f0d97d' },
    danger:  { bg: '#fde7e9', fg: '#a4262c', border: '#f1b9bd' },
    neutral: { bg: '#f3f2f1', fg: '#4a4844', border: '#d2d0ce' },
};

const pillClass = (tone: Tone) => mergeStyles({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 12,
    lineHeight: '16px',
    fontWeight: 600,
    backgroundColor: tones[tone].bg,
    color: tones[tone].fg,
    border: `1px solid ${tones[tone].border}`,
    whiteSpace: 'nowrap',
});

const iconClass = mergeStyles({ fontSize: 12 });

export interface PillProps {
    tone: Tone;
    iconName?: string;
    role?: string;
    ariaLabel?: string;
    children: React.ReactNode;
}

export const Pill: React.FC<PillProps> = ({ tone, iconName, role, ariaLabel, children }) => (
    <span className={pillClass(tone)} role={role} aria-label={ariaLabel}>
        {iconName && <Icon iconName={iconName} className={iconClass} aria-hidden="true" />}
        {children}
    </span>
);

export default Pill;
